import { CurrencyPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticsTariff, PostpaidCurrentPlanItem, Tier } from '../../../shared/models/pricing.model';
import { AuthUser } from '../../../shared/models/user.model';
import { NumberFormatPipe } from '../../../shared/pipes/number-format.pipe';
import { NotificationService } from '../../../shared/services/notification.service';
import { PricingService } from '../../../shared/services/pricing.service';
import { UsersService } from '../../../shared/services/users.service';
import { Part } from '../../billing-progress/billing-progress.component';

@Component({
  standalone: false,
  selector: 'app-special-pricing-group-client',
  templateUrl: './special-pricing-group-client.component.html',
  styleUrls: ['special-pricing-group-client.component.scss'],
})
export class SpecialPricingGroupClientComponent implements OnInit {
  loading = false;
  tiers: Tier[] = [];
  user: AuthUser;

  currentBalance = 0;
  testsCount = 0;
  commitmentMt = 0;

  itemsMt: PostpaidCurrentPlanItem[] = [];
  progressMt: Progress;
  itemsMo: PostpaidCurrentPlanItem[] = [];
  progressMo: Progress;
  itemsVoice: PostpaidCurrentPlanItem[] = [];
  progressVoice: Progress;
  itemsFlashCall: PostpaidCurrentPlanItem[] = [];
  progressFlashCall: Progress;

  analyticsTariff: AnalyticsTariff;
  private planUpdate = false;

  constructor(
    public pricingService: PricingService,
    public userService: UsersService,
    public router: Router,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    private numberFormat: NumberFormatPipe,
    private currency: CurrencyPipe,
    titleService: Title
  ) {
    titleService.setTitle('Billing');
  }

  ngOnInit() {
    this.loading = true;
    this.userService.getAuthUser().then(user => {
      this.user = user;
      this.userService.balanceUpdates.subscribe(_ => this.update());
    });
  }

  private update() {
    if (this.planUpdate) {
      return;
    }
    this.planUpdate = true;
    this.pricingService.getPostpaidPlan().subscribe({
      next: data => {
        this.planUpdate = false;
        this.currentBalance =
          (this.getUsageSum(data.itemsMt) +
            this.getUsageSum(data.itemsMo) +
            this.getUsageSum(data.itemsVoice) +
            this.getUsageSum(data.itemsFlashCall ?? [])) *
          -1;
        this.commitmentMt = this.calculateCommitment(data.itemsMt);
        this.testsCount = data.testsCount;
        this.itemsMt = this.cumulativeItems(data.itemsMt);
        this.progressMt = this.createProgress(this.itemsMt);
        this.itemsMo = this.cumulativeItems(data.itemsMo);
        this.progressMo = this.createProgress(this.itemsMo);
        this.itemsVoice = this.cumulativeItems(data.itemsVoice);
        this.progressVoice = this.createProgress(this.itemsVoice);
        this.itemsFlashCall = this.cumulativeItems(data.itemsFlashCall ?? []);
        this.progressFlashCall = this.createProgress(this.itemsFlashCall);
        if (data.plan.analyticsTariff) {
          data.plan.analyticsTariff.rate = data.plan.analyticsTariff.rate / 10;
          this.currentBalance -= data.plan.analyticsTariff.rate;
        }
        this.analyticsTariff = data.plan.analyticsTariff;
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this.planUpdate = false;
        console.error(err);
      },
    });
  }

  private cumulativeItems(items: PostpaidCurrentPlanItem[]): PostpaidCurrentPlanItem[] {
    let usageTests = 0;
    return items.map(item => {
      usageTests += item.usageTests;
      return { ...item, usageTests };
    });
  }

  private createProgress(items: PostpaidCurrentPlanItem[]): Progress {
    if (!items.length) return null;
    if (items.length === 1) {
      return {
        total: items[0].usageTests,
        current: items[0].usageTests,
        label: this.createLabel(items[0], items),
        parts: [{ max: items[0].usagePercent }],
      };
    }
    const parts = items.map(_ => _);
    const progress = {
      total: 0,
      current: items.map(_ => _.usageTests).reduce((a, b) => a + b, 0),
      label: this.createLabel(parts.filter(part => part.usageTests)[0], parts),
      parts: parts.map(item => {
        return {
          max: isFinite(item.max) ? item.max : item.min,
          partUsagePercent: item.usagePercent,
        };
      }),
    };
    progress.total = progress.parts.length * 100;
    progress.current = parts.map(_ => _.usagePercent).reduce((a, b) => a + b);
    return progress;
  }

  private createLabel(item: PostpaidCurrentPlanItem, items: PostpaidCurrentPlanItem[]): string {
    if (!item || !item.usageTests) {
      return '';
    }
    const totalTests = items[items.length - 1].usageTests;
    let ratePerTest;
    if (items[items.length - 1].usagePercent === 100) {
      ratePerTest = items[items.length - 1].ratePerTest;
    } else {
      ratePerTest = items.find(i => i.usagePercent > 0 && i.usagePercent < 100).ratePerTest;
    }
    return `${this.numberFormat.transform(totalTests)} tests sent calculated at €${ratePerTest}/test`;
  }

  private calculateCommitment(items: PostpaidCurrentPlanItem[]): number {
    let commitment = 0;
    items.forEach(item => {
      if (!item.usageTests) {
        return;
      }
      if (item.commitment && item.commitment > commitment) {
        commitment = item.commitment;
      }
    });
    if (!commitment && items.length && items[0].commitment) {
      return items[0].commitment;
    }
    return commitment;
  }

  public getUsageSum(items: PostpaidCurrentPlanItem[]): number {
    let lastItemWithTests: PostpaidCurrentPlanItem = null;
    let usageTests = 0;
    items.forEach(i => {
      if (i.usageTests) {
        lastItemWithTests = i;
      }
      usageTests += i.usageTests;
    });
    if (lastItemWithTests) {
      return usageTests * lastItemWithTests.ratePerTest;
    }
    return 0;
  }

  getFullNameServiceType(serviceTypeCode: string): string {
    const map = {
      MT: 'Mobile terminated',
      API: 'Api',
      MO: 'Mobile Originated',
      STATISTICS: '',
    };
    if (map[serviceTypeCode]) {
      return map[serviceTypeCode];
    }
    return serviceTypeCode;
  }

  public getRatePerTest(items: PostpaidCurrentPlanItem[], index: number): string {
    return this.currency.transform(items[index].ratePerTest, 'EUR', 'symbol', '1.2-3');
  }

  public getCurrentAmountSpentOnTier(items: PostpaidCurrentPlanItem[]): string {
    let amount = 0;
    for (const item of items) {
      if (item.usagePercent < 100 || item.isLast) {
        amount = item.ratePerTest * item.usageTests;
        break;
      }
    }
    const transformedAmount = this.currency.transform(amount, 'EUR', 'symbol', '1.2-3');
    return amount > 0 ? '-' + transformedAmount : transformedAmount;
  }
}

interface Progress {
  displayPercent?: number;
  total: number;
  current: number;
  label: string;
  parts: Part[];
}
